import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Send, Copy, X, Loader2, Eye, LogOut } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { useApi, errMsg, fmtDate } from "@/lib/api";
import { useApp } from "@/lib/app-context";

function MemberRow({ l, onRemove, onPerm }) {
  const pending = l.status === "pending";
  return (
    <div className="flex flex-col gap-2 border-b border-ww-border/70 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between" data-testid={`family-member-${l.id}`}>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-ww-navy">{l.member_name || l.email}</p>
        <p className="text-xs text-ww-muted">{l.email} · {pending ? `Invite pending, expires ${fmtDate(l.expires_at?.slice(0, 10))}` : `Viewing since ${fmtDate(l.accepted_at?.slice(0, 10))}`}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!pending && <label className="flex items-center gap-2 text-xs font-semibold text-ww-navy"><Switch checked={!!l.can_edit} onCheckedChange={(v) => onPerm(l, v)} data-testid={`family-can-edit-${l.id}`} />Can add & edit</label>}
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${pending ? "bg-amber-50 text-amber-700" : l.can_edit ? "bg-sky-50 text-sky-700" : "bg-ww-mint text-ww-emerald"}`} data-testid={`family-member-status-${l.id}`}>{pending ? "Pending" : l.can_edit ? "Editor" : "View only"}</span>
        <button onClick={() => onRemove(l)} className="flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid={`family-remove-${l.id}`}><X className="h-3.5 w-3.5" />{pending ? "Cancel" : "Remove"}</button>
      </div>
    </div>
  );
}

function SharedWithMe({ items, onLeave }) {
  if (!items.length) return null;
  return (
    <div className="mt-8" data-testid="family-shared-with-me">
      <h3 className="font-display text-lg font-semibold text-ww-navy">Wallets shared with you</h3>
      {items.map((l) => (
        <div key={l.id} className="mt-3 flex flex-col gap-2 rounded-2xl bg-ww-cyan/50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`family-shared-${l.id}`}>
          <p className="text-sm font-semibold text-ww-navy">{l.owner_name}'s household wallet</p>
          <div className="flex gap-2">
            <Link to={`/shared/${l.owner_id}`} className="ww-btn-ghost" data-testid={`family-open-shared-${l.id}`}><Eye className="h-4 w-4" />Open</Link>
            <button onClick={() => onLeave(l)} className="ww-btn-ghost text-red-600" data-testid={`family-leave-${l.id}`}><LogOut className="h-4 w-4" />Leave</button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function FamilySharing() {
  const api = useApi();
  const { me } = useApp();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const load = useCallback(() => api("get", "/family").then(setData).catch(() => setData({ members: [], shared_with_me: [], max_members: 5 })), [api]);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api("post", "/family/invites", { email });
      setLink({ url: r.join_url, id: r.invite.id });
      toast.success(r.email_sent ? `Invitation emailed to ${email}` : "Invite created. Email could not be sent, share the link below.");
      setEmail("");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const remove = async (l) => {
    setConfirm(null);
    if (link?.id === l.id) setLink(null);
    try { await api("delete", `/family/links/${l.id}`); toast.success(l.owner_id === me.user_id ? "Access removed" : "You left the shared wallet"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const setPerm = async (l, can_edit) => {
    try { await api("patch", `/family/links/${l.id}`, { can_edit }); toast.success(can_edit ? `${l.member_name || l.email} can now add and update products` : `${l.member_name || l.email} is now view-only`); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  if (!data) return <Loader2 className="mx-auto h-6 w-6 animate-spin text-ww-emerald" />;
  const full = data.members.length >= data.max_members;
  return (
    <section className="rounded-[26px] border border-ww-border bg-white p-6 sm:p-8" data-testid="account-family-panel">
      <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ww-navy"><Users className="h-5 w-5 text-ww-emerald" />Family Sharing</h2>
      <p className="mt-2 text-sm text-ww-muted">Invite up to {data.max_members} family members to view your household wallet: products, coverage periods, documents and expiry reminders. Members are view-only unless you turn on "Can add & edit", which lets a trusted member add and update products and upload documents (using your plan's records). Nobody but you can delete anything.</p>
      <form onSubmit={invite} className="mt-6 flex flex-col gap-3 sm:flex-row">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="family.member@email.com" className="ww-input w-full sm:flex-1" disabled={!me.email_verified || full} data-testid="family-invite-email-input" />
        <button className="ww-btn-primary" disabled={busy || !me.email_verified || full} data-testid="family-invite-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}Send Invite</button>
      </form>
      {!me.email_verified && <p className="mt-2 text-xs text-amber-700" data-testid="family-verify-note">Verify your email address to invite family members.</p>}
      {full && <p className="mt-2 text-xs text-ww-muted" data-testid="family-full-note">You've reached the {data.max_members}-member limit.</p>}
      {link && (
        <div className="mt-4 flex items-center gap-2 rounded-2xl bg-ww-mint/60 p-3 text-xs" data-testid="family-invite-link">
          <span className="min-w-0 flex-1 truncate text-ww-navy/80">{link.url}</span>
          <button type="button" onClick={() => navigator.clipboard.writeText(link.url).then(() => toast.success("Link copied"))} className="flex items-center gap-1 font-semibold text-ww-emerald" data-testid="family-copy-link"><Copy className="h-3.5 w-3.5" />Copy</button>
        </div>
      )}
      <div className="mt-6">
        {data.members.length === 0 ? <p className="rounded-2xl bg-ww-mint/40 px-4 py-6 text-center text-sm text-ww-muted" data-testid="family-empty">No one has access to your wallet yet.</p>
          : data.members.map((l) => <MemberRow key={l.id} l={l} onRemove={setConfirm} onPerm={setPerm} />)}
      </div>
      <SharedWithMe items={data.shared_with_me} onLeave={setConfirm} />
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="rounded-[24px]" data-testid="family-confirm-dialog">
          <AlertDialogTitle>{confirm?.owner_id === me.user_id ? (confirm?.status === "pending" ? "Cancel this invitation?" : "Remove access?") : "Leave this wallet?"}</AlertDialogTitle>
          <AlertDialogDescription>{confirm?.owner_id === me.user_id ? `${confirm?.member_name || confirm?.email} will no longer be able to view your wallet.` : `You will stop seeing ${confirm?.owner_name}'s products and reminders.`}</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel data-testid="family-confirm-cancel">Keep</AlertDialogCancel><AlertDialogAction onClick={() => remove(confirm)} className="bg-red-600 hover:bg-red-700" data-testid="family-confirm-ok">Confirm</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
